// SPDX-License-Identifier: Apache-2.0

import React from "react";
import "./i18n/i18n";
import "./App.css";
import logo from "./images/perun_logo.png";
import { Nav, Navbar, NavLink } from "react-bootstrap";
import { AppContext, PerunAppState } from "./AppContext";
import InfoModal, { InfoProps } from "./components/dialogs/InfoModal";
import { Client } from "./client/Client";
import { PerunNotice } from "./events/Notice";
import { useTranslation } from "react-i18next";
import { HelpMenu } from "./components/HelpMenu";
import PrivacyPolicy from "./components/PrivacyPolicy";
import SwapScreen from "./components/SwapScreen";
import LiquidityScreen from "./components/LiquidityScreen";
import History from "./components/History";
import ErrorBoundary from "./components/ErrorBoundary";
import Contact from "./components/Contact";
import ErrorModal, { ErrorProps } from "./components/dialogs/ErrorModal";
import ContactInfo from "./components/ContactInfo";
// Onboarding_combined is the cross-chain (Ethereum + CKB) flow and the only one
// this build ships. Onboarding_mainnet is the legacy Ethereum-only product; it
// cannot onboard a CKB wallet, so it is not reachable here. The network a build
// targets is decided at build time by src/config.json, which the Docker build
// overwrites from configs/<REACT_APP_CONFIG>.json, and
// constants/CKBNetwork.ts, not by the user.
import { Onboarding_combined } from "./components/Onboarding_combined";
import {Navigator} from "./components/Navigator";
import CKBProvider from "./components/CKBProvider";
import { ckbNetworkName } from "./constants/CKBNetwork";
import { lpEnabled } from "./constants/Constants";
import { ccc } from "@ckb-ccc/connector-react";
// Imported directly rather than via ccc.SignerType: the connector-react
// namespace re-exports it through a chain of "export *" barrels shared by
// every wallet-connector sub-package (eip6963, joy-id, uni-sat, …), and at
// least one of those re-exports collides with it — TypeScript drops a name
// re-exported ambiguously by "export *" rather than erroring on the barrel
// itself, so ccc.SignerType silently does not exist at the top level even
// though the enum is real and other files import from this package fine.
import { SignerType } from "@ckb-ccc/core";
import FinishSwap from "./components/FinishSwap";

export type Page =
  | "NAVIGATOR"
  | "ONBOARDING"
  | "SWAP"
  | "POOL"
  | "HISTORY"
  | "FINISH";

function App(this: any) {
  const { t } = useTranslation();
  const [page, setPage] = React.useState<Page>("NAVIGATOR");
  // We use a separate state for the show property of InfoProps to prevent the
  // info modal from updating to the new content first before actually hiding.
  const [hasInfo, setShowInfo] = React.useState(false);
  const [hasError, setShowError] = React.useState(false);
  const [infoProps, setInfoProps] = React.useState<InfoProps>({
    show: false,
    toggleShow: () => {},
  });
  const [constInfoProps, setConstInfoProps] = React.useState<ErrorProps>({
    show: false,
  });
  const [client, setClient] = React.useState<Client>();

  const handleError = (ev: CustomEventInit<JSX.Element>) => {
    setInfoProps({
      show: hasInfo,
      toggleShow: () => setShowInfo(false),
      title: t("error"),
      content: ev.detail,
    });
    setShowInfo(true);
  };

  const handleOpenError = (ev: CustomEventInit<JSX.Element>) => {
    setConstInfoProps({
      show: hasError,
      title: t("error"),
      content: ev.detail,
    });
    setShowError(true);
  };

  const handleCloseError = (ev: CustomEventInit<JSX.Element>) => {
    setShowError(false);
  };

  const handleNotice = (ev: CustomEventInit<PerunNotice>) => {
    setInfoProps({
      show: hasInfo,
      toggleShow: () => setShowInfo(false),
      title: ev.detail?.title,
      content: ev.detail?.content,
      submitText: ev.detail?.submitText,
      onSubmit: ev.detail?.onSubmit,
      cancelText: ev.detail?.cancelText,
      onCancel: ev.detail?.onCancel,
      autoClose: ev.detail?.autoClose,
    });
    setShowInfo(true);
  };

  const handleCloseNotice = (ev: CustomEventInit<JSX.Element>) => {
    setShowInfo(false);
  };

  const handleNavigateToFinish = () => {
    setShowInfo(false);
    setPage("FINISH");
  };

  const initClient = (client: Client) => {
    setClient(client);
  };

  const ctx = {
    client: client,
    setClient: initClient,
  } as PerunAppState;

  React.useEffect(() => {
    document.addEventListener("PerunError", handleError);
    document.addEventListener("PerunNotice", handleNotice);
    document.addEventListener("ClosePerunNotice", handleCloseNotice);
    document.addEventListener("OpenMetamaskError", handleOpenError);
    document.addEventListener("CloseMetamaskError", handleCloseError);
    document.addEventListener("NavigateToFinish", handleNavigateToFinish);
    return () => {
      document.removeEventListener("PerunError", handleError);
      document.removeEventListener("PerunNotice", handleNotice);
      document.removeEventListener("ClosePerunNotice", handleCloseNotice);
      document.removeEventListener("OpenMetamaskError", handleOpenError);
      document.removeEventListener("CloseMetamaskError", handleCloseError);
      document.removeEventListener("NavigateToFinish", handleNavigateToFinish);
    };
  });

  const getPage = (): JSX.Element => {
    switch (page) {
      case "NAVIGATOR":
        return (
          <Navigator setPage={(page: Page) => setPage(page)} />
        );
      case "ONBOARDING":
        localStorage.setItem("site", "ONBOARDING");
        return (
          <Onboarding_combined
            toggleOnboarded={() => {
              setPage("SWAP");
            }}
          />
        );
      case "SWAP":
        /*if (
          window.localStorage.getItem("site") === "HISTORY" &&
          ctx.client.provider.getChainID.toString() !==
            window.localStorage.getItem("chainID")
        ) {
          dispatchMetamaskError(
            <p>
              {t("errorUnexpectedNetwork", {
                currID: ctx.client.provider.getChainID.toString(),
                chainID: window.localStorage.getItem("chainID"),
              })}
            </p>
          );
        }*/
        localStorage.setItem("site", "SWAP");
        return <SwapScreen />;
      case "POOL":
        // A page persisted by an earlier build must not open a screen this
        // build does not offer.
        if (!lpEnabled) {
          localStorage.setItem("site", "SWAP");
          return <SwapScreen />;
        }
        localStorage.setItem("site", "POOL");
        return <LiquidityScreen />;
      case "HISTORY":
        localStorage.setItem("site", "HISTORY");
        return <History />;
      case "FINISH":
        localStorage.setItem("site", "FINISH");
        return <FinishSwap
          initial={localStorage.getItem("signedState") || ""}
        />;
    }
  };

  return (
    <>
      <ErrorBoundary>
        <ccc.Provider
          // Perun-X derives every CKB identity from the connected Ethereum
          // key (OmniLock auth flag 0x12; Provider.ts recovers the pubkey via
          // ethers.utils.hashMessage), so only EVM-style signers can ever
          // produce a usable signature here, and MetaMask is the only wallet
          // the app supports and is tested with. Without this filter the
          // connector offers BTC/Nostr/native-CKB wallets (JoyID, UniSat,
          // OKX's BTC signer, …) and every other injected EVM wallet; picking
          // one fails deep inside the swap flow instead of at connect time.
          signerFilter={async (signerInfo, wallet) =>
            signerInfo.signer.type === SignerType.EVM &&
            /metamask/i.test(wallet.name)
          }
        >
          <CKBProvider>
            <Navbar className="navbar sticky-top">
              <div className="w-100 navbar-grid">
                {/* LEFT: brand + page links */}
                <div className="d-flex align-items-center gap-3">
                  <Navbar.Brand
                    onClick={() => {
                      if (page !== "ONBOARDING") setPage("SWAP");
                    }}
                    href="#"
                    style={{ color: "#00C9B8" }}
                  >
                    <img id="perun-logo" width="30" src={logo} alt="" />
                    <span className="d-none d-sm-inline navbar-brand">
                      {t("title")}
                    </span>
                  </Navbar.Brand>

                  <Nav>
                    <NavLink
                      active={page === "ONBOARDING"}
                      onClick={() => {
                        window.localStorage.removeItem("site");
                        window.localStorage.removeItem("clientState");
                        window.localStorage.removeItem("currentSwap");
                        window.localStorage.removeItem("chainID");
                        window.localStorage.removeItem("wsRequestCounter");
                        if (ctx.client) {
                          ctx.client.shutdownClient();
                        }
                        setPage("ONBOARDING");
                      }}
                    >
                      {t("onboard")}
                    </NavLink>
                    {/* Swap needs an onboarded wallet, nothing else: it used to be
                        disabled on FINISH too, which — together with History —
                        left a user who arrived on Finish Swap (the recovery
                        notice navigates there, and the page is persisted across
                        reloads) with Onboard as the only working link. */}
                    <NavLink
                      disabled={page === "ONBOARDING"}
                      active={page === "SWAP"}
                      onClick={() => setPage("SWAP")}
                    >
                      {t("swap")}
                    </NavLink>
                    {lpEnabled && (
                      <NavLink
                        disabled={page === "ONBOARDING" || page === "FINISH"}
                        active={page === "POOL"}
                        onClick={() => setPage("POOL")}
                      >
                        {t("pool")}
                      </NavLink>
                    )}
                    {/* History is read-only; there is no state it could disturb. */}
                    <NavLink
                      active={page === "HISTORY"}
                      onClick={() => setPage("HISTORY")}
                    >
                      {t("history")}
                    </NavLink>
                    <NavLink
                      disabled={page === "SWAP"}
                      active={page === "FINISH"}
                      onClick={() => setPage("FINISH")}
                    >
                      {t("recoveryNav")}
                    </NavLink>
                  </Nav>
                </div>

                {/* CENTER: which network this build targets.
                    This was a Testnet/Mainnet switch. It could not do what its
                    labels promised: "Testnet" selected the cross-chain flow on
                    config_cross.json (Sepolia), "Mainnet" selected the legacy
                    Ethereum-only onboarding, and neither position was Ethereum
                    mainnet paired with CKB mainnet. The network is fixed when
                    the bundle is built, so it is now shown, not offered. */}
                <div className="d-flex justify-content-center align-items-center gap-2">
                  <span className="network-label" style={{ fontWeight: "bold" }}>
                    {ckbNetworkName}
                  </span>
                </div>

                {/* RIGHT: help + status */}
                <div className="d-flex justify-content-end align-items-center gap-3">
                  <AppContext.Provider value={ctx}>
                    <HelpMenu />
                  </AppContext.Provider>
                  <div className="StatusPane">
                    {page !== "ONBOARDING" ? t("connected") : t("notConnected")}
                  </div>
                </div>
              </div>
            </Navbar>
            <div className="App">
              <AppContext.Provider value={ctx}>{getPage()}</AppContext.Provider>
            </div>
            <div className="flex scroll">
              <PrivacyPolicy />
              <ContactInfo />
              <div className="BottomLeft">
                <Contact />
                <p className="first-child">{t("TokenSwap")}</p>
                <p>{t("Blockchains")}</p>
              </div>
            </div>
            <InfoModal
              show={hasInfo}
              toggleShow={infoProps.toggleShow}
              title={infoProps.title}
              content={infoProps.content}
              submitText={infoProps.submitText}
              onSubmit={infoProps.onSubmit}
              cancelText={infoProps.cancelText}
              onCancel={infoProps.onCancel}
              autoClose={infoProps.autoClose}
            />
            <ErrorModal
              show={hasError}
              title={constInfoProps.title}
              content={constInfoProps.content}
            />
          </CKBProvider>
        </ccc.Provider>
      </ErrorBoundary>
    </>
  );
}

export default App;
